import React, { useState, useEffect, useRef } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Brain, Save, Trash2, Calendar, MapPin, Sparkles, TrendingUp, Hash, Target, Upload, Image, Loader2, Download, Share2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ResultadoLinha {
  ppt: string;
  ptm: string;
  pt: string;
  ptv: string;
  ptn: string;
  cor: string;
}

interface ResultadoSalvo {
  id: string;
  estado: string;
  data: string;
  resultados: ResultadoLinha[];
  createdAt: string;
}

interface Previsoes {
  milhares1a5: string[];   // 3 milhares 1º ao 5º
  milharesPP: string[];    // 5 milhares PP
  centenas1a5: string[];   // 7 centenas 1º ao 5º
  dataValidade: string;    // Data de validade (dia todo)
}

interface TendenciaUnidade {
  unidadeMilhar: Record<string, number>;
  unidadeCentena: Record<string, number>;
  unidadeDezena: Record<string, number>;
  unidade: Record<string, number>;
  dezenaInicial: Record<string, number>;
  dezenaFinal: Record<string, number>;
}

const STORAGE_KEY = "jogo_bicho_ai_resultados";

const estadosDisponiveis = [
  { value: "para-todos", label: "Para Todos - Rio" },
  { value: "federal", label: "Federal" },
  { value: "rj", label: "Rio de Janeiro" },
  { value: "sp", label: "São Paulo" },
  { value: "go", label: "Goiás" },
  { value: "ba", label: "Bahia" },
  { value: "mg", label: "Minas Gerais" },
  { value: "pb", label: "Paraíba" },
];

const colunas = ["PPT", "PTM", "PT", "PTV", "PTN", "COR"];
const linhasLabels = ["1º Prêmio", "2º Prêmio", "3º Prêmio", "4º Prêmio", "5º Prêmio", "6º Prêmio", "7º Prêmio"];

const JogoBichoAI: React.FC = () => {
  const [estado, setEstado] = useState("para-todos");
  const [data, setData] = useState(() => {
    const hoje = new Date();
    return hoje.toISOString().split('T')[0];
  });
  const [resultados, setResultados] = useState<ResultadoLinha[]>(() => 
    Array(7).fill(null).map(() => ({
      ppt: "", ptm: "", pt: "", ptv: "", ptn: "", cor: ""
    }))
  );
  const [historico, setHistorico] = useState<ResultadoSalvo[]>([]);
  const [previsoes, setPrevisoes] = useState<Previsoes | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carregar histórico do localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setHistorico(JSON.parse(saved));
      } catch (e) {
        console.error("Erro ao carregar histórico:", e);
      }
    }
  }, []);

  // Formatar resultado: XXXX-XX
  const formatarResultado = (value: string): string => {
    const numeros = value.replace(/\D/g, '');
    if (numeros.length > 4) {
      return numeros.slice(0, 4) + '-' + numeros.slice(4, 6);
    }
    return numeros;
  };

  // Atualizar valor de uma célula
  const atualizarCelula = (linha: number, coluna: keyof ResultadoLinha, valor: string) => {
    const novoResultados = [...resultados];
    novoResultados[linha] = {
      ...novoResultados[linha],
      [coluna]: formatarResultado(valor)
    };
    setResultados(novoResultados);
  };

  // Processar upload de imagem
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Arquivo inválido",
        description: "Por favor, selecione uma imagem (PNG, JPG, etc.)",
        variant: "destructive"
      });
      return;
    }

    // Validar tamanho (máx 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "A imagem deve ter no máximo 10MB",
        variant: "destructive"
      });
      return;
    }

    setIsExtracting(true);

    try {
      // Converter para base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remover o prefixo data:image/xxx;base64,
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Mostrar preview
      setPreviewImage(`data:${file.type};base64,${base64}`);

      toast({
        title: "🔍 Analisando imagem...",
        description: "Extraindo resultados automaticamente, aguarde."
      });

      // Chamar edge function
      const { data: response, error } = await supabase.functions.invoke('extract-bicho-results', {
        body: { imageBase64: base64 }
      });

      if (error) {
        throw new Error(error.message || 'Erro ao processar imagem');
      }

      if (response?.error) {
        throw new Error(response.error);
      }

      if (response?.success && response?.resultados) {
        // Preencher o formulário com os resultados extraídos
        const novosResultados = response.resultados.map((r: any) => ({
          ppt: r.ppt || "",
          ptm: r.ptm || "",
          pt: r.pt || "",
          ptv: r.ptv || "",
          ptn: r.ptn || "",
          cor: r.cor || ""
        }));

        // Garantir que temos 7 linhas
        while (novosResultados.length < 7) {
          novosResultados.push({ ppt: "", ptm: "", pt: "", ptv: "", ptn: "", cor: "" });
        }

        setResultados(novosResultados.slice(0, 7));

        toast({
          title: "✅ Resultados extraídos!",
          description: "Os dados foram preenchidos automaticamente. Confira e salve."
        });
      } else {
        throw new Error('Não foi possível extrair os resultados');
      }

    } catch (error: any) {
      console.error('Erro ao extrair resultados:', error);
      toast({
        title: "Erro na extração",
        description: error.message || "Não foi possível extrair os resultados da imagem. Tente novamente ou preencha manualmente.",
        variant: "destructive"
      });
    } finally {
      setIsExtracting(false);
      // Limpar input para permitir novo upload do mesmo arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Salvar resultados no histórico
  const salvarResultados = () => {
    // Verificar se há pelo menos um resultado preenchido
    const temResultado = resultados.some(linha => 
      Object.values(linha).some(v => v.length > 0)
    );

    if (!temResultado) {
      toast({
        title: "Atenção",
        description: "Preencha pelo menos um resultado antes de salvar.",
        variant: "destructive"
      });
      return;
    }

    const novoResultado: ResultadoSalvo = {
      id: Date.now().toString(),
      estado,
      data,
      resultados,
      createdAt: new Date().toISOString()
    };

    const novoHistorico = [novoResultado, ...historico].slice(0, 50); // Mantém últimos 50
    setHistorico(novoHistorico);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(novoHistorico));

    // Limpar formulário
    setResultados(Array(7).fill(null).map(() => ({
      ppt: "", ptm: "", pt: "", ptv: "", ptn: "", cor: ""
    })));

    toast({
      title: "✅ Resultados Salvos!",
      description: `Resultados de ${data} salvos com sucesso.`
    });
  };

  // Limpar histórico
  const limparHistorico = () => {
    if (confirm("Tem certeza que deseja limpar todo o histórico?")) {
      setHistorico([]);
      localStorage.removeItem(STORAGE_KEY);
      setPrevisoes(null);
      toast({
        title: "Histórico Limpo",
        description: "Todo o histórico foi removido."
      });
    }
  };

  // Extrair apenas a milhar (sem o grupo do bicho)
  const extrairMilhar = (valor: string): string | null => {
    if (!valor || valor.length < 4) return null;
    const milhar = valor.split('-')[0];
    return milhar && milhar.length === 4 ? milhar : null;
  };

  // Analisar tendências de unidades
  const analisarTendencias = (): TendenciaUnidade => {
    const tendencia: TendenciaUnidade = {
      unidadeMilhar: {},
      unidadeCentena: {},
      unidadeDezena: {},
      unidade: {},
      dezenaInicial: {},
      dezenaFinal: {}
    };

    historico.forEach(registro => {
      registro.resultados.forEach((linha) => {
        Object.values(linha).forEach(valor => {
          const milhar = extrairMilhar(valor);
          if (milhar) {
            // Unidade de milhar (primeiro dígito)
            const uMilhar = milhar[0];
            tendencia.unidadeMilhar[uMilhar] = (tendencia.unidadeMilhar[uMilhar] || 0) + 1;

            // Unidade de centena (segundo dígito)
            const uCentena = milhar[1];
            tendencia.unidadeCentena[uCentena] = (tendencia.unidadeCentena[uCentena] || 0) + 1;

            // Unidade de dezena (terceiro dígito)
            const uDezena = milhar[2];
            tendencia.unidadeDezena[uDezena] = (tendencia.unidadeDezena[uDezena] || 0) + 1;

            // Unidade (último dígito)
            const unidade = milhar[3];
            tendencia.unidade[unidade] = (tendencia.unidade[unidade] || 0) + 1;

            // Dezena inicial (primeiros 2 dígitos)
            const dezInicial = milhar.slice(0, 2);
            tendencia.dezenaInicial[dezInicial] = (tendencia.dezenaInicial[dezInicial] || 0) + 1;

            // Dezena final (últimos 2 dígitos)
            const dezFinal = milhar.slice(2, 4);
            tendencia.dezenaFinal[dezFinal] = (tendencia.dezenaFinal[dezFinal] || 0) + 1;
          }
        });
      });
    });

    return tendencia;
  };

  // Obter top N de um objeto de frequência
  const getTopN = (freq: Record<string, number>, n: number): string[] => {
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, n)
      .map(([num]) => num);
  };

  // Gerar milhar baseada nas tendências
  const gerarMilharFromTendencias = (tendencia: TendenciaUnidade, usados: Set<string>): string => {
    const topMilhar = getTopN(tendencia.unidadeMilhar, 5);
    const topCentena = getTopN(tendencia.unidadeCentena, 5);
    const topDezena = getTopN(tendencia.unidadeDezena, 5);
    const topUnidade = getTopN(tendencia.unidade, 5);
    const topDezInicial = getTopN(tendencia.dezenaInicial, 10);
    const topDezFinal = getTopN(tendencia.dezenaFinal, 10);

    let tentativas = 0;
    let milhar = "";

    while (tentativas < 100) {
      // Alterna entre usar dezenas ou unidades individuais
      if (Math.random() > 0.5 && topDezInicial.length > 0 && topDezFinal.length > 0) {
        // Usar dezenas iniciais e finais
        const dezInicial = topDezInicial[Math.floor(Math.random() * Math.min(topDezInicial.length, 5))];
        const dezFinal = topDezFinal[Math.floor(Math.random() * Math.min(topDezFinal.length, 5))];
        milhar = dezInicial + dezFinal;
      } else {
        // Usar unidades individuais
        const m = topMilhar[Math.floor(Math.random() * Math.min(topMilhar.length, 3))] || String(Math.floor(Math.random() * 10));
        const c = topCentena[Math.floor(Math.random() * Math.min(topCentena.length, 3))] || String(Math.floor(Math.random() * 10));
        const d = topDezena[Math.floor(Math.random() * Math.min(topDezena.length, 3))] || String(Math.floor(Math.random() * 10));
        const u = topUnidade[Math.floor(Math.random() * Math.min(topUnidade.length, 3))] || String(Math.floor(Math.random() * 10));
        milhar = m + c + d + u;
      }

      if (!usados.has(milhar) && milhar.length === 4) {
        usados.add(milhar);
        return milhar;
      }
      tentativas++;
    }

    // Fallback: gerar número aleatório único
    do {
      milhar = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
    } while (usados.has(milhar));
    usados.add(milhar);
    return milhar;
  };

  // Gerar centena baseada nas tendências (últimos 3 dígitos)
  const gerarCentenaFromTendencias = (tendencia: TendenciaUnidade, usados: Set<string>): string => {
    const topCentena = getTopN(tendencia.unidadeCentena, 5);
    const topDezena = getTopN(tendencia.unidadeDezena, 5);
    const topUnidade = getTopN(tendencia.unidade, 5);
    const topDezFinal = getTopN(tendencia.dezenaFinal, 10);

    let tentativas = 0;
    let centena = "";

    while (tentativas < 100) {
      if (Math.random() > 0.4 && topDezFinal.length > 0) {
        // Usar dezena final + unidade
        const dezFinal = topDezFinal[Math.floor(Math.random() * Math.min(topDezFinal.length, 5))];
        const c = topCentena[Math.floor(Math.random() * Math.min(topCentena.length, 3))] || String(Math.floor(Math.random() * 10));
        centena = c + dezFinal;
      } else {
        const c = topCentena[Math.floor(Math.random() * Math.min(topCentena.length, 3))] || String(Math.floor(Math.random() * 10));
        const d = topDezena[Math.floor(Math.random() * Math.min(topDezena.length, 3))] || String(Math.floor(Math.random() * 10));
        const u = topUnidade[Math.floor(Math.random() * Math.min(topUnidade.length, 3))] || String(Math.floor(Math.random() * 10));
        centena = c + d + u;
      }

      if (!usados.has(centena) && centena.length === 3) {
        usados.add(centena);
        return centena;
      }
      tentativas++;
    }

    // Fallback
    do {
      centena = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    } while (usados.has(centena));
    usados.add(centena);
    return centena;
  };

  // Gerar previsões baseadas no histórico
  const gerarPrevisoes = () => {
    if (historico.length < 1) {
      toast({
        title: "Dados Insuficientes",
        description: "Adicione pelo menos 1 dia de resultados para gerar previsões.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);

    setTimeout(() => {
      const tendencia = analisarTendencias();
      
      // Coletar milhares de primeiros prêmios (PP)
      const milharesPP: string[] = [];
      historico.forEach(registro => {
        const linha = registro.resultados[0]; // 1º prêmio
        if (linha) {
          Object.values(linha).forEach(valor => {
            const milhar = extrairMilhar(valor);
            if (milhar) milharesPP.push(milhar);
          });
        }
      });

      // Coletar milhares de 1º ao 5º prêmio
      const milhares1a5: string[] = [];
      historico.forEach(registro => {
        registro.resultados.slice(0, 5).forEach((linha) => {
          Object.values(linha).forEach(valor => {
            const milhar = extrairMilhar(valor);
            if (milhar) milhares1a5.push(milhar);
          });
        });
      });

      // Contar frequência
      const contarFrequencia = (arr: string[]) => {
        const freq: Record<string, number> = {};
        arr.forEach(n => {
          freq[n] = (freq[n] || 0) + 1;
        });
        return Object.entries(freq)
          .sort((a, b) => b[1] - a[1])
          .map(([num]) => num);
      };

      const ppFreq = contarFrequencia(milharesPP);
      const freq1a5 = contarFrequencia(milhares1a5);

      // Gerar 3 milhares para 1º ao 5º (baseado em tendências)
      const usadosMilhar = new Set<string>();
      const milharesGerados1a5: string[] = [];
      
      // Primeiro, adicionar os mais frequentes
      freq1a5.slice(0, 2).forEach(m => {
        if (milharesGerados1a5.length < 3 && !usadosMilhar.has(m)) {
          milharesGerados1a5.push(m);
          usadosMilhar.add(m);
        }
      });
      
      // Completar com geração baseada em tendências
      while (milharesGerados1a5.length < 3) {
        milharesGerados1a5.push(gerarMilharFromTendencias(tendencia, usadosMilhar));
      }

      // Gerar 5 milhares para PP
      const milharesGeradosPP: string[] = [];
      const usadosPP = new Set<string>();
      
      // Primeiro, adicionar os mais frequentes de PP
      ppFreq.slice(0, 3).forEach(m => {
        if (milharesGeradosPP.length < 5 && !usadosPP.has(m)) {
          milharesGeradosPP.push(m);
          usadosPP.add(m);
        }
      });
      
      // Completar com geração baseada em tendências
      while (milharesGeradosPP.length < 5) {
        milharesGeradosPP.push(gerarMilharFromTendencias(tendencia, usadosPP));
      }

      // Gerar 7 centenas para 1º ao 5º
      const centenasGeradas: string[] = [];
      const usadasCentenas = new Set<string>();
      
      while (centenasGeradas.length < 7) {
        centenasGeradas.push(gerarCentenaFromTendencias(tendencia, usadasCentenas));
      }

      // Data de validade (hoje)
      const hoje = new Date();
      const dataValidade = hoje.toLocaleDateString('pt-BR', { 
        weekday: 'long', 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });

      const novasPrevisoes: Previsoes = {
        milhares1a5: milharesGerados1a5,
        milharesPP: milharesGeradosPP,
        centenas1a5: centenasGeradas,
        dataValidade
      };

      setPrevisoes(novasPrevisoes);
      setIsGenerating(false);

      toast({
        title: "🎯 Previsões Geradas!",
        description: `Análise de tendências concluída. Jogos válidos para o dia todo!`
      });
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <PageInstructions
        title="📖 Como usar o Sistema IA Bicho"
        steps={[
          "Insira os resultados dos últimos sorteios nos campos indicados",
          "Escolha o turno e a loteria que deseja jogar",
          "Selecione quantos palpites deseja gerar",
          "Clique em 'Gerar Palpites com IA' e aguarde o processamento",
          "A IA analisa padrões e tendências para gerar palpites inteligentes",
          "Veja os resultados com grupos, dezenas, centenas e milhares sugeridos"
        ]}
      />
      {/* Header */}
      <div className="text-center p-4 bg-gradient-to-r from-green-500 to-yellow-500 rounded-xl">
        <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center justify-center gap-2">
          <Brain className="w-8 h-8" />
          Sistema IA - Jogo do Bicho
        </h2>
        <p className="text-white/90 mt-1">Análise inteligente de resultados e geração de previsões</p>
      </div>

      {/* Configuração */}
      <Card className="border-2 border-green-300">
        <CardHeader className="bg-gradient-to-r from-green-50 to-yellow-50">
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <MapPin className="w-5 h-5 text-green-600" />
            Configuração do Sorteio
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estado" className="text-gray-700 font-semibold">Estado/Região</Label>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="bg-white text-gray-900 border-gray-300 mt-1">
                  <SelectValue placeholder="Selecione o estado" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                  {estadosDisponiveis.map(e => (
                    <SelectItem key={e.value} value={e.value} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="data" className="text-gray-700 font-semibold">Data do Sorteio</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="data"
                  type="date"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                  className="bg-white text-gray-900 border-gray-300 pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload de Imagem */}
      <Card className="border-2 border-indigo-300">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <Upload className="w-5 h-5 text-indigo-600" />
            Upload de Resultados (OCR Automático)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              📸 Envie uma foto da tabela de resultados e os dados serão preenchidos automaticamente!
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 items-start">
              {/* Área de Upload */}
              <div className="flex-1 w-full">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={isExtracting}
                />
                <label
                  htmlFor="image-upload"
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                    isExtracting 
                      ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
                      : 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400'
                  }`}
                >
                  {isExtracting ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                      <span className="text-sm text-gray-600">Extraindo resultados...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Image className="w-8 h-8 text-indigo-500" />
                      <span className="text-sm font-medium text-gray-700">Clique ou arraste uma imagem</span>
                      <span className="text-xs text-gray-500">PNG, JPG até 10MB</span>
                    </div>
                  )}
                </label>
              </div>

              {/* Preview da Imagem */}
              {previewImage && (
                <div className="w-full md:w-48 shrink-0">
                  <p className="text-xs text-gray-500 mb-1">Última imagem:</p>
                  <img 
                    src={previewImage} 
                    alt="Preview" 
                    className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                  />
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              💡 <strong>Dica:</strong> Para melhores resultados, envie uma foto nítida da tabela de resultados completa, como a do exemplo (7 prêmios x 6 turnos).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Entrada de Resultados */}
      <Card className="border-2 border-yellow-400">
        <CardHeader className="bg-gradient-to-r from-yellow-100 to-yellow-200">
          <CardTitle className="flex items-center justify-between text-gray-800">
            <div className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-yellow-700" />
              Entrada de Resultados (Formato: XXXX-XX)
            </div>
            <div className="flex items-center gap-2 text-sm font-normal bg-yellow-50 px-3 py-1 rounded-full border border-yellow-300">
              <Calendar className="w-4 h-4 text-yellow-600" />
              <span className="text-yellow-800">{new Date(data).toLocaleDateString('pt-BR')}</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 md:p-4 overflow-x-auto">
          <div className="min-w-[480px]">
            {/* Cabeçalho da Tabela */}
            <div className="grid grid-cols-7 gap-0.5 md:gap-1 mb-2">
              <div className="bg-yellow-300 p-1 md:p-2 text-center font-bold text-gray-800 rounded text-[10px] md:text-sm">
                Prêmio
              </div>
              {colunas.map(col => (
                <div key={col} className="bg-yellow-300 p-1 md:p-2 text-center font-bold text-gray-800 rounded text-[10px] md:text-sm">
                  {col}
                </div>
              ))}
            </div>

            {/* Linhas de Resultados */}
            {resultados.map((linha, idx) => (
              <div key={idx} className="grid grid-cols-7 gap-0.5 md:gap-1 mb-1">
                <div className="bg-yellow-100 p-1 md:p-2 text-center font-semibold text-gray-700 rounded text-[10px] md:text-sm flex items-center justify-center">
                  {linhasLabels[idx]}
                </div>
                {(Object.keys(linha) as Array<keyof ResultadoLinha>).map(col => (
                  <Input
                    key={col}
                    type="text"
                    value={linha[col]}
                    onChange={(e) => atualizarCelula(idx, col, e.target.value)}
                    placeholder="0000-00"
                    maxLength={7}
                    className="bg-yellow-50 text-gray-900 border-yellow-300 text-center text-[10px] md:text-sm p-0.5 md:p-2 placeholder:text-gray-400 font-mono"
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Botão Salvar */}
          <div className="mt-4 flex justify-center">
            <Button
              onClick={salvarResultados}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-8 py-3"
            >
              <Save className="w-5 h-5 mr-2" />
              Salvar Resultados
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Resultados */}
      <Card className="border-2 border-blue-300">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-gray-800">
              <Calendar className="w-5 h-5 text-blue-600" />
              Histórico de Resultados ({historico.length} registros)
            </CardTitle>
            {historico.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={limparHistorico}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {historico.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              Nenhum resultado salvo ainda. Adicione resultados acima para começar a análise.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left text-gray-700">Data</th>
                    <th className="p-2 text-left text-gray-700">Estado</th>
                    <th className="p-2 text-left text-gray-700">1º Prêmio (PPT)</th>
                    <th className="p-2 text-left text-gray-700">Registros</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.slice(0, 10).map(registro => (
                    <tr key={registro.id} className="border-b border-gray-200 hover:bg-blue-50">
                      <td className="p-2 text-gray-800">{new Date(registro.data).toLocaleDateString('pt-BR')}</td>
                      <td className="p-2 text-gray-800">{estadosDisponiveis.find(e => e.value === registro.estado)?.label || registro.estado}</td>
                      <td className="p-2 text-gray-800 font-mono">{registro.resultados[0]?.ppt || '-'}</td>
                      <td className="p-2 text-gray-600">{registro.resultados.filter(r => Object.values(r).some(v => v)).length} linhas</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {historico.length > 10 && (
                <p className="text-center text-gray-500 text-sm mt-2">
                  E mais {historico.length - 10} registros...
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botão Gerar Previsões */}
      <div className="flex justify-center">
        <Button
          onClick={gerarPrevisoes}
          disabled={isGenerating || historico.length < 1}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold px-10 py-4 text-lg shadow-lg"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full" />
              Gerando Previsões...
            </>
          ) : (
            <>
              <Brain className="w-6 h-6 mr-2" />
              Gerar Previsões IA
            </>
          )}
        </Button>
      </div>

      {historico.length < 1 && (
        <p className="text-center text-amber-600 text-sm">
          ⚠️ Adicione pelo menos 1 dia de resultados para gerar previsões (recomendado: 3 dias)
        </p>
      )}
      
      {historico.length >= 1 && historico.length < 3 && (
        <p className="text-center text-blue-600 text-sm">
          💡 Dica: Com mais dias de resultados, as previsões ficam mais precisas
        </p>
      )}

      {/* Previsões Geradas */}
      {previsoes && (
        <div className="space-y-4">
          {/* Header com validade e botões */}
          <div className="text-center p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
            <h3 className="text-xl font-bold text-white flex items-center justify-center gap-2">
              <Sparkles className="w-6 h-6" />
              Jogos Gerados pela IA
            </h3>
            <div className="mt-2 inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
              <Calendar className="w-4 h-4 text-white" />
              <span className="text-white font-medium">
                Válidos para: {previsoes.dataValidade} (DIA TODO)
              </span>
            </div>
            
            {/* Botões Compartilhar e Baixar */}
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => {
                  const texto = `🎯 SISTEMA IA - JOGO DO BICHO\n📅 Válido para: ${previsoes.dataValidade}\n\n` +
                    `📊 3 MILHARES (1º ao 5º):\n${previsoes.milhares1a5.join('\n')}\n\n` +
                    `🎯 5 MILHARES PP:\n${previsoes.milharesPP.join('\n')}\n\n` +
                    `📌 7 CENTENAS (1º ao 5º):\n${previsoes.centenas1a5.join('\n')}\n\n` +
                    `🤖 Gerado por IA Palpite Guru`;
                  
                  if (navigator.share) {
                    navigator.share({
                      title: 'Sistema IA - Jogo do Bicho',
                      text: texto
                    }).catch(console.error);
                  } else {
                    navigator.clipboard.writeText(texto);
                    toast({
                      title: "📋 Copiado!",
                      description: "Jogos copiados para a área de transferência"
                    });
                  }
                }}
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Compartilhar
              </Button>
              
              <Button
                onClick={() => {
                  const texto = `SISTEMA IA - JOGO DO BICHO\nVálido para: ${previsoes.dataValidade}\n\n` +
                    `3 MILHARES (1º ao 5º):\n${previsoes.milhares1a5.join('\n')}\n\n` +
                    `5 MILHARES PP:\n${previsoes.milharesPP.join('\n')}\n\n` +
                    `7 CENTENAS (1º ao 5º):\n${previsoes.centenas1a5.join('\n')}\n\n` +
                    `Gerado por IA Palpite Guru`;
                  
                  const blob = new Blob([texto], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `sistema-ia-bicho-${new Date().toISOString().split('T')[0]}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  
                  toast({
                    title: "📥 Baixado!",
                    description: "Arquivo de jogos salvo com sucesso"
                  });
                }}
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Jogos
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 3 Milhares 1º ao 5º */}
            <Card className="border-2 border-purple-400 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 py-3">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <TrendingUp className="w-5 h-5" />
                  3 Milhares (1º ao 5º)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {previsoes.milhares1a5.map((m, i) => (
                    <div key={i} className="bg-purple-100 text-purple-900 px-4 py-3 rounded-lg text-center font-mono text-xl font-bold border-2 border-purple-200">
                      {m}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 5 Milhares PP */}
            <Card className="border-2 border-green-400 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 py-3">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <Target className="w-5 h-5" />
                  5 Milhares PP
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {previsoes.milharesPP.map((p, i) => (
                    <div key={i} className="bg-green-100 text-green-900 px-4 py-3 rounded-lg text-center font-mono text-xl font-bold border-2 border-green-200">
                      {p}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 7 Centenas 1º ao 5º */}
            <Card className="border-2 border-cyan-400 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-cyan-500 to-cyan-600 py-3">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <Hash className="w-5 h-5" />
                  7 Centenas (1º ao 5º)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-2">
                  {previsoes.centenas1a5.map((c, i) => (
                    <div key={i} className={`bg-cyan-100 text-cyan-900 px-3 py-3 rounded-lg text-center font-mono text-lg font-bold border-2 border-cyan-200 ${i === 6 ? 'col-span-2' : ''}`}>
                      {c}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Metodologia */}
          <Card className="border border-gray-300 bg-gradient-to-r from-gray-50 to-slate-50">
            <CardContent className="p-4">
              <h4 className="font-semibold text-gray-700 mb-2">📊 Metodologia da Análise de Tendências:</h4>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Análise de repetição de <strong>unidade de milhar</strong> (1º dígito) nos últimos {historico.length} sorteios</li>
                <li>Análise de repetição de <strong>unidade de centena</strong> (2º dígito)</li>
                <li>Análise de repetição de <strong>unidade de dezena</strong> (3º dígito)</li>
                <li>Análise de repetição de <strong>unidade</strong> (último dígito)</li>
                <li>Combinação de <strong>dezenas iniciais</strong> e <strong>dezenas finais</strong> mais frequentes</li>
                <li>⚠️ Grupo do bicho (após o traço) foi <strong>excluído</strong> da análise</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JogoBichoAI;
